// Live Colour Tiles artwork (DESIGN.md): flat, code-drawn illustrations that sit inside the colour
// tiles. Masses in the tile's ink colour with a translucent shade for depth; no strokes thinner than
// 2 units, so they hold up at small sizes on budget screens. Decorative: the tile carries the label.
import { Platform } from "react-native";
import Svg, { Circle, G, Path, Rect } from "react-native-svg";

type ArtProps = {
  /** The tile's ink (white on blue/pink/green, near-black on amber). */
  ink: string;
  size?: number;
};

const hidden =
  Platform.OS === "web"
    ? { "aria-hidden": true }
    : {
        accessible: false,
        importantForAccessibility: "no-hide-descendants" as const,
      };

/** Three books stacked, the top one open-edged: School books. */
export function BookStackArt({ ink, size = 96 }: ArtProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 96 96" {...hidden}>
      <G fill={ink}>
        <Rect x="10" y="62" width="72" height="16" />
        <Rect x="16" y="44" width="64" height="16" opacity={0.85} />
        <Rect x="8" y="26" width="68" height="16" opacity={0.7} />
      </G>
      <G fill={ink} opacity={0.35}>
        <Rect x="10" y="62" width="10" height="16" />
        <Rect x="16" y="44" width="10" height="16" />
        <Rect x="8" y="26" width="10" height="16" />
      </G>
      <Path d="M82 66h6v8h-6z" fill={ink} opacity={0.5} />
    </Svg>
  );
}

/** A pencil laid at an angle over a ruled line: Stationery. */
export function PencilArt({ ink, size = 96 }: ArtProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 96 96" {...hidden}>
      <G transform="rotate(-38 48 48)">
        <Rect x="14" y="40" width="54" height="16" fill={ink} />
        <Rect x="14" y="48" width="54" height="8" fill={ink} opacity={0.75} />
        <Path d="M68 40 L86 48 L68 56 Z" fill={ink} opacity={0.6} />
        <Path d="M80 45.3 L86 48 L80 50.7 Z" fill={ink} />
        <Rect x="6" y="40" width="8" height="16" fill={ink} opacity={0.45} />
      </G>
      <Rect x="12" y="82" width="72" height="3" fill={ink} opacity={0.35} />
    </Svg>
  );
}

/** A folded school shirt with a tie: Uniforms. */
export function ShirtArt({ ink, size = 96 }: ArtProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 96 96" {...hidden}>
      <Path
        d="M30 14 L40 20 H56 L66 14 L86 28 L78 44 L70 40 V84 H26 V40 L18 44 L10 28 Z"
        fill={ink}
        opacity={0.8}
      />
      <Path d="M40 20 L48 30 L56 20 Z" fill={ink} opacity={0.45} />
      <Path d="M45 30 H51 L54 58 L48 66 L42 58 Z" fill={ink} />
    </Svg>
  );
}

/** A school building with a flag: the school's own notice. */
export function SchoolArt({ ink, size = 120 }: ArtProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 120 120" {...hidden}>
      <Rect x="58" y="10" width="3" height="26" fill={ink} />
      <Path d="M61 10 h20 l-6 6 l6 6 h-20 z" fill={ink} opacity={0.7} />
      <Path d="M20 54 L60 34 L100 54 Z" fill={ink} />
      <Rect x="26" y="54" width="68" height="50" fill={ink} opacity={0.85} />
      <Rect x="12" y="104" width="96" height="6" fill={ink} />
      <G fill={ink} opacity={0.4}>
        <Rect x="34" y="62" width="12" height="12" />
        <Rect x="74" y="62" width="12" height="12" />
        <Rect x="34" y="82" width="12" height="12" />
        <Rect x="74" y="82" width="12" height="12" />
        <Rect x="53" y="78" width="14" height="26" />
      </G>
    </Svg>
  );
}

/** A small dot-matrix patch for a feature tile's corner (the world's one texture). */
export function DotMatrix({ ink, size = 56 }: ArtProps) {
  const dots = [];
  for (let r = 0; r < 6; r += 1) {
    for (let c = 0; c < 6; c += 1) {
      dots.push(
        <Circle key={`${r}-${c}`} cx={4 + c * 9.6} cy={4 + r * 9.6} r={1.6} />,
      );
    }
  }
  return (
    <Svg width={size} height={size} viewBox="0 0 56 56" {...hidden}>
      <G fill={ink} opacity={0.45}>
        {dots}
      </G>
    </Svg>
  );
}
